import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Tooltip, ICON } from '@attendance/ui';
import { focusRing } from '@attendance/ui/focus';

/* ===========================================================================
 * THE EXPLANATION, ONE HOVER AWAY.
 *
 * ── THE SPLIT THIS EXISTS TO MAKE ───────────────────────────────────────────
 *
 * A form field can carry two kinds of sentence and they have opposite lives. A
 * WARNING — an obstacle, a consequence, something that changes what you type —
 * has to be on screen at the moment of acting, every time. An EXPLANATION —
 * what this field is for, why it is shaped this way — answers a question a
 * reader has once and is furniture on every visit after.
 *
 * Written the same way, stacked under thirteen fields, the second kind buries
 * the first: the ERP connection screen was a document with inputs in it, and
 * the two lines that genuinely stop a mistake read like the eleven that do not
 * (owner: "reduce hints … we can use tips to clean up").
 *
 * So the explanations move behind this marker and the warnings stay put.
 *
 * ── IT IS A BUTTON, AND IT OPENS ON FOCUS ───────────────────────────────────
 *
 * Not a bare icon with a `title`. `Tooltip` opens on focus as well as hover and
 * wires `aria-describedby`, so the words are reachable by keyboard and read by
 * a screen reader as a description of the control they belong to. A tip nobody
 * can open is a hint that was deleted, and the whole point of moving these
 * sentences is that they are still there.
 *
 * Its accessible name is "What is this?", never the tip's own text: the reader
 * hears the button, then the description it points at — naming it after its
 * content would say the same sentence twice.
 * ======================================================================== */

export function TipButton({ tip, side }: { tip: string; side?: 'top' | 'bottom' | 'left' | 'right' }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Tooltip content={tip} {...(side ? { side } : {})}>
      <button
        type="button"
        aria-label={t('common.whatIsThis', 'What is this?')}
        className={`flex size-4 shrink-0 items-center justify-center rounded-full text-foreground-tertiary transition-colors duration-fast hover:text-foreground ${focusRing}`}
      >
        <Info size={ICON.inline} aria-hidden />
      </button>
    </Tooltip>
  );
}
